/**
 * Design System Animation Tokens
 * Consistent motion for delightful interactions
 */

// Duration tokens
export const duration = {
  instant: '0ms',
  fast: '150ms',
  base: '200ms',
  moderate: '300ms',
  slow: '400ms',
  slower: '600ms',
};

// Easing curves
export const easing = {
  // Standard easing curves
  linear: 'linear',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Specialized curves
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

// Common transitions
export const transitions = {
  // Base transitions
  all: `all ${duration.base} ${easing.smooth}`,
  colors: `background-color ${duration.base} ${easing.smooth}, border-color ${duration.base} ${easing.smooth}, color ${duration.base} ${easing.smooth}`,
  transform: `transform ${duration.base} ${easing.smooth}`,
  opacity: `opacity ${duration.base} ${easing.smooth}`,
  shadow: `box-shadow ${duration.base} ${easing.smooth}`,
  
  // Component-specific transitions
  button: {
    base: `background-color ${duration.fast} ${easing.smooth}, border-color ${duration.fast} ${easing.smooth}, color ${duration.fast} ${easing.smooth}, transform ${duration.fast} ${easing.smooth}, box-shadow ${duration.fast} ${easing.smooth}`,
    active: `transform ${duration.instant} ${easing.smooth}`,
  },
  
  input: {
    base: `border-color ${duration.base} ${easing.smooth}, box-shadow ${duration.base} ${easing.smooth}`,
    error: `border-color ${duration.fast} ${easing.smooth}`,
  },
  
  card: {
    hover: `transform ${duration.base} ${easing.smooth}, box-shadow ${duration.base} ${easing.smooth}`,
  },
  
  modal: {
    backdrop: `opacity ${duration.moderate} ${easing.smooth}`,
    content: `opacity ${duration.moderate} ${easing.smooth}, transform ${duration.moderate} ${easing.smooth}`,
  },
  
  dropdown: {
    open: `opacity ${duration.fast} ${easing.smooth}, transform ${duration.fast} ${easing.smooth}`,
  },
  
  tooltip: {
    show: `opacity ${duration.fast} ${easing.smooth}, transform ${duration.fast} ${easing.smooth}`,
  },
};

// Keyframe animations
export const keyframes = {
  fadeIn: `
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  `,
  
  fadeOut: `
    @keyframes fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }
  `,
  
  slideInUp: `
    @keyframes slideInUp {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `,
  
  slideInDown: `
    @keyframes slideInDown {
      from {
        transform: translateY(-10px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `,
  
  scaleIn: `
    @keyframes scaleIn {
      from {
        transform: scale(0.95);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }
  `,
  
  pulse: `
    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
      100% {
        transform: scale(1);
      }
    }
  `,
  
  shake: `
    @keyframes shake {
      0%, 100% {
        transform: translateX(0);
      }
      10%, 30%, 50%, 70%, 90% {
        transform: translateX(-2px);
      }
      20%, 40%, 60%, 80% {
        transform: translateX(2px);
      }
    }
  `,
  
  spin: `
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,
};

// Animation utilities
export const animations = {
  fadeIn: `fadeIn ${duration.base} ${easing.smooth}`,
  fadeOut: `fadeOut ${duration.base} ${easing.smooth}`,
  slideInUp: `slideInUp ${duration.moderate} ${easing.smooth}`,
  slideInDown: `slideInDown ${duration.moderate} ${easing.smooth}`,
  scaleIn: `scaleIn ${duration.base} ${easing.smooth}`,
  pulse: `pulse ${duration.slower} ${easing.smooth} infinite`,
  shake: `shake ${duration.moderate} ${easing.linear}`,
  spin: `spin ${duration.slower} ${easing.linear} infinite`,
}; 